import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { DetroitGameState } from '../../types';
import { selectActionDetroit, skipAction } from '../moves/selectActionsDetroit';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 3: アクション選択フェーズ（Detroit固有）
 *
 * ソロプレイヤーは3種類のアクション（Engineer, Locomotive, Urbanization）から1つを選択します。
 * 各アクションは3回まで選択可能（ディスク制）。
 * 選択時に手数料を支払う必要があります（$3/$2/$1、ディスク残数に応じて）。
 *
 * @see docs/specifications/logic/detroit_bankruptcy_solo/phase_03_select_actions_override.md
 */
export const selectActionsDetroitPhase: PhaseConfig<DetroitGameState> = {
  moves: {
    selectAction: selectActionDetroit,
    skipAction: skipAction,
    undoToTurnStart,
  },

  // Phase開始時の処理
  onBegin: ({ G }: { G: DetroitGameState }) => {
    debug.log(`[selectActionsDetroitPhase] Phase 3: Select Actions started`);
    debug.log(`[selectActionsDetroitPhase] Available disks: ${JSON.stringify(G.actionDisks)}`);

    // 前ターンのパスフラグをリセット
    const soloPlayer = G.players['0'];
    if (soloPlayer) {
      soloPlayer.hasPassed = false;
    }

    // 全ディスクが使用済みかチェック
    const totalDisks = G.actionDisks.ENGINEER + G.actionDisks.LOCOMOTIVE + G.actionDisks.URBANIZATION;
    if (totalDisks === 0) {
      debug.log(`[selectActionsDetroitPhase] WARNING: All action disks used up!`);
      // 全ディスクが使用済みの場合、アクションなしで進行
      // この場合はPhase 4に直接遷移
    }
  },

  // Phase終了条件: ソロプレイヤーがアクション選択完了またはパス、または Undo 強制遷移
  endIf: ({ G }: { G: DetroitGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log(`[selectActionsDetroitPhase] Force transition to issueShares (Undo)`);
      return true;
    }

    const soloPlayer = G.players['0'];

    // プレイヤーがアクションを選択した場合
    const hasSelectedAction = soloPlayer && soloPlayer.selectedAction !== null;

    // プレイヤーがパスした場合
    const hasPassed = soloPlayer && soloPlayer.hasPassed;

    // または全ディスクが使用済みの場合（アクションなしで進行）
    const allDisksUsed = G.actionDisks.ENGINEER === 0 &&
                         G.actionDisks.LOCOMOTIVE === 0 &&
                         G.actionDisks.URBANIZATION === 0;

    if (hasSelectedAction) {
      debug.log(`[selectActionsDetroitPhase] Phase ending, selected action: ${soloPlayer.selectedAction}`);
    }
    if (hasPassed) {
      debug.log(`[selectActionsDetroitPhase] Phase ending (player passed)`);
    }
    if (allDisksUsed && !hasSelectedAction && !hasPassed) {
      debug.log(`[selectActionsDetroitPhase] Phase ending (all disks used, no action)`);
    }

    return hasSelectedAction || hasPassed || allDisksUsed;
  },

  // Phase終了時の処理
  onEnd: () => {
    debug.log(`[selectActionsDetroitPhase] Phase ended, transitioning to buildTrack`);
  },

  // Phase 4 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: DetroitGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'buildTrack';
  }
};
